import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import type {
  ServiceStatus,
  StatusHistoryBucket,
} from '../api/systemStatus';

/**
 * Compact uptime timeline strip — statuspage.io-style.
 *
 * Each bucket renders as a fixed-height vertical bar; color comes
 * from the bucket's rolled-up status. None ("unknown") segments
 * paint as a subtle gray so gaps are visible without screaming.
 *
 * The strip is hoverable: each bar shows the bucket start +
 * humanized status on hover. Display is bucket-agnostic — the
 * component doesn't care whether the caller asked for 5-min or
 * 60-min buckets; it just lines up what came back.
 */

interface Props {
  buckets: StatusHistoryBucket[];
  /** Used to format the tooltip suffix; falls back to a generic
   *  "1h window" when omitted. */
  bucketMinutes?: number;
}

const COLOR_FOR_STATUS: Record<ServiceStatus, string> = {
  ok: '#16a34a', // green-600
  stale: '#eab308', // yellow-500
  down: '#dc2626', // red-600
};

// "Unknown" buckets (no samples) paint as a very subtle gray so the
// operator can tell the difference between "we measured ok" and "we
// didn't measure at all" (sampler down, fresh deploy, etc).
const UNKNOWN_COLOR = '#e5e7eb'; // gray-200

export function StatusHistoryStrip({ buckets, bucketMinutes = 60 }: Props) {
  if (buckets.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        No history yet
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.25} sx={{ height: 28, minWidth: 200 }}>
      {buckets.map((b) => (
        <BucketBar
          key={b.bucket_start}
          bucket={b}
          bucketMinutes={bucketMinutes}
        />
      ))}
    </Stack>
  );
}

function BucketBar({
  bucket,
  bucketMinutes,
}: {
  bucket: StatusHistoryBucket;
  bucketMinutes: number;
}) {
  const color = bucket.status
    ? COLOR_FOR_STATUS[bucket.status]
    : UNKNOWN_COLOR;
  const ts = new Date(bucket.bucket_start);
  const tsLabel = ts.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: bucketMinutes < 60 ? 'numeric' : undefined,
  });
  const statusLabel = bucket.status ? bucket.status : 'no data';
  return (
    <Tooltip
      title={`${tsLabel} — ${statusLabel}`}
      placement="top"
      arrow
      disableInteractive
    >
      <Box
        sx={{
          flex: '1 1 0',
          minWidth: 2,
          bgcolor: color,
          borderRadius: 0.5,
          cursor: 'help',
          transition: 'opacity 0.15s',
          '&:hover': { opacity: 0.7 },
        }}
        aria-label={`${tsLabel}: ${statusLabel}`}
      />
    </Tooltip>
  );
}
